import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronRight, Plus, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { aceitarConvite, createEscritorio, fetchEscritorios } from '@/api/client';
import { ConvitesPendentes } from '@/components/escritorio/ConvitesPendentes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Fab } from '@/components/ui/Fab';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useConvitesPendentes, useEntrarNoEscritorio } from '@/hooks/useConvitesPendentes';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

import { EscritorioFormDialog } from './EscritorioFormDialog';
import { RoleBadge } from './papeis';

/**
 * Os escritórios do usuário (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md, Bloco 4a). Cada card
 * abre a página do escritório — membros, equipes e convites deixaram de ser um modal.
 * Também é a entrada do link de convite (`?token=`). Com um escritório só, a lista não tem o
 * que escolher: abre direto nele (o voltar de lá traz `?todos=1`, para criar outro).
 */
export default function EscritoriosListaPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [criarAberto, setCriarAberto] = useState(false);

  const {
    data: escritorios,
    isLoading,
    error,
  } = useQuery({
    queryKey: [...queryKeys.escritorios.all],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });

  // O link do e-mail de convite chega aqui com ?token=; aceito, abre o escritório.
  const token = searchParams.get('token');
  const entrar = useEntrarNoEscritorio();
  const pendentes = useConvitesPendentes(!token);
  // Um aceite por token: o StrictMode (dev) monta duas vezes, e o segundo aceite falharia.
  const aceito = useRef<string | null>(null);
  useEffect(() => {
    if (!token || aceito.current === token) return;
    aceito.current = token;
    aceitarConvite(token)
      .then(entrar)
      .catch((err) => {
        notifyMutationError(err, showError);
        setSearchParams({}, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const criar = useMutation({
    mutationFn: createEscritorio,
    onSuccess: (esc) => {
      showSuccess('Escritório criado');
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
      setCriarAberto(false);
      navigate(`/app/escritorios/${esc.id}`);
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  // Com convite pendente, a lista fica: é aqui que ele aparece.
  const semConvite = !pendentes.isLoading && (pendentes.data?.length ?? 0) === 0;
  const unico =
    !token && semConvite && searchParams.get('todos') !== '1' && escritorios?.length === 1 ? escritorios[0] : null;
  if (unico) {
    return <Navigate to={`/app/escritorios/${unico.id}`} replace />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Building2 />}
        title="Escritórios"
        subtitle="Os escritórios de que você faz parte"
        actions={
          <Button onClick={() => setCriarAberto(true)} className="hidden sm:inline-flex">
            <Plus className="mr-2 h-4 w-4" />
            Novo escritório
          </Button>
        }
      />
      <Fab icon={<Plus className="h-6 w-6" />} label="Novo escritório" onClick={() => setCriarAberto(true)} />

      {!token && <ConvitesPendentes />}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {isLoading || token ? (
        <div className="flex justify-center py-12">
          <Spinner size={32} label={token ? 'Aceitando o convite' : 'Carregando escritórios'} />
        </div>
      ) : escritorios && escritorios.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {escritorios.map((esc) => (
            <Card
              key={esc.id}
              role="link"
              tabIndex={0}
              aria-label={`Abrir ${esc.nome}`}
              className="cursor-pointer transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => navigate(`/app/escritorios/${esc.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/app/escritorios/${esc.id}`);
                }
              }}
            >
              <CardContent className="flex h-full flex-col gap-3 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Building2 className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-words font-semibold">{esc.nome}</p>
                    {esc.descricao && (
                      <p className="mt-0.5 line-clamp-2 break-words text-xs text-muted-foreground">{esc.descricao}</p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                </div>
                <div className="mt-auto flex items-center justify-between gap-2">
                  <RoleBadge role={esc.meu_role} />
                  {esc.total_membros !== undefined && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3.5 w-3.5" aria-hidden="true" />
                      {esc.total_membros} {esc.total_membros === 1 ? 'membro' : 'membros'}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Building2 />}
          title="Nenhum escritório"
          description="Crie um escritório para trabalhar com os colegas no mesmo acervo."
          action={{
            label: 'Criar escritório',
            onClick: () => setCriarAberto(true),
          }}
        />
      )}

      <EscritorioFormDialog
        open={criarAberto}
        onOpenChange={setCriarAberto}
        salvando={criar.isPending}
        onSalvar={(dados) => criar.mutate(dados)}
      />
    </div>
  );
}
